/**
 * StarterCard — one of the 5 starter recipes per segment. Clicking it
 * navigates to `/spark?starter=<id>` so Step 1 can pre-fill the form
 * with the matching segment's `engineDomain` and a sensible default
 * name derived from the starter id.
 */

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';

import { cn } from '@/utils/cn';
import type { Segment, SegmentStarter } from '@/data/segments';
import { lookupStarterProduct } from '@/data/productPrefills';

export function StarterCard({
  segment,
  starter,
}: {
  segment: Segment;
  starter: SegmentStarter;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation('segments');
  const ready = segment.status === 'ready';

  // Coming-Soon segments still let the user navigate to Spark, but
  // pre-fill is best-effort — Step 1 will fall back to a Generic
  // engine domain when no adapter exists.
  // If the starter is wired to a commercial product (construction-,
  // hr-, realestate-), route via `?product=<slug>` so the richer
  // product prefill takes precedence over the bare starter form.
  const handleClick = () => {
    const productSlug = lookupStarterProduct(segment.id, starter.id);
    if (productSlug) {
      navigate(`/spark?product=${productSlug}`);
      return;
    }
    navigate(`/spark?starter=${starter.id}&segment=${segment.id}`);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid={`starter-card-${segment.id}-${starter.id}`}
      className={cn(
        'group flex h-full flex-col items-start gap-2 rounded-lg border border-border bg-surface p-4 text-start',
        'transition-colors hover:border-spark hover:bg-spark-soft/40',
        'focus-visible:border-spark',
      )}
    >
      <div className="flex w-full items-center justify-between gap-2">
        <span
          className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-spark-soft text-spark"
          aria-hidden
        >
          <Sparkles className="h-3.5 w-3.5" strokeWidth={2} />
        </span>
        {!ready ? (
          <span className="text-[10px] font-medium uppercase tracking-wider text-ink-subtle">
            {t('starter.preview_only')}
          </span>
        ) : null}
      </div>
      <h4 className="text-sm font-semibold text-ink">
        {t(`starters.${segment.id}.${starter.id}.name`)}
      </h4>
      <p className="text-xs text-ink-muted">
        {t(`starters.${segment.id}.${starter.id}.oneliner`)}
      </p>
      <span className="mt-auto pt-1 text-xs font-medium text-spark group-hover:underline">
        {t('starter.use_cta')} →
      </span>
    </button>
  );
}
